import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { GridApi, GridReadyEvent, GridState } from 'ag-grid-enterprise';
import { useRateTypeStore } from '@/store/rate-type-store';
import { store as globalStore } from '@/store/store';
import { TradingPermission } from '@/utils/helpers/trading-permissions';
import { CompanyInfo, Security } from '@/modules/common/models';
import { Side } from '@/connect/gen/consts/commonconsts_pb';
import { TopOfBookWatchlist } from '@/modules/marketplace/models';
import { AgreementInfo } from '@/modules/agreements/models';
import { OmsOrderType, OrderIntent, SettlementType } from '@/connect';
import { DataSourceConfig } from '@/modules/marketplace/services/datasource-explorer';

export const masterFilterItems = [
  { value: 'security', text: 'Specific security' },
  { value: 'securities', text: 'List of securities' },
  { value: 'watchlist', text: 'Securities on a Watch List' },
  { value: 'company', text: 'Orders against a Company' },
  { value: 'agreement', text: 'Orders against an Agreement' },
  { value: 'settlement', text: 'Orders against a Settlement Type' },
  { value: 'activeAndCompany', text: 'Potential Matches against a Company' },
  { value: 'activeAndAgreement', text: 'Potential Matches against an Agreement' },
  { value: 'activeAndSettlement', text: 'Potential Matches against a Settlement Type' },
] as const;

export type MasterFilterType = (typeof masterFilterItems)[number]['value'];

export interface Filters {
  tradeIntention: Side;
  security: Security | null;
  securities: Security[];
  orderType: OmsOrderType;
  watchlist: TopOfBookWatchlist | null;
  companies: CompanyInfo[];
  agreements: AgreementInfo[];
  settlementType: SettlementType;
  selectedCompany: CompanyInfo | null;
  orderIntent: OrderIntent | null;
}

export const useGridApiExplorerStore = defineStore('gridApiExplorer', () => {
  const gridApi = shallowRef<GridApi | null>(null);
  const areGroupsExpanded = shallowRef(true);
  const isGridLoaded = computed(() => gridApi.value !== null);
  const gridFilterModel = ref<Record<string, unknown> | null>(null);

  const initialState = shallowRef<GridState | null>(null);

  const rateTypeStore = useRateTypeStore();

  const asBroker = shallowRef(false);
  const ordersPageLimit = shallowRef(500);
  const selectedMasterFilter = shallowRef<MasterFilterType | null>(null);

  function getDefaultSide(): Side {
    return globalStore.getters.canLend ? Side.LOAN : Side.BORROW;
  }

  function getEmptyFilters(
    selectedTradeIntention: Side | null,
    selectedCompany: CompanyInfo | null
  ): Filters {
    // only change Side when not yet initialized
    const tradeIntention =
      selectedTradeIntention !== null ? selectedTradeIntention : getDefaultSide();

    return {
      tradeIntention: tradeIntention,
      security: null,
      securities: [],
      orderType: OmsOrderType.UNSPECIFIED_ORDER_TYPE,
      watchlist: null,
      companies: [],
      agreements: [],
      settlementType: SettlementType.UNSPECIFIED,
      orderIntent: null,
      selectedCompany,
    };
  }

  const filters = ref<Filters>(getEmptyFilters(null, null));

  const canLend = computed(() =>
    !asBroker.value
      ? globalStore.getters.canLend
      : ((filters.value?.selectedCompany?.tradingPermissions ?? 0) &
          TradingPermission.LenderPermissions) >
        0
  );

  const canBorrow = computed(() =>
    !asBroker.value
      ? globalStore.getters.canBorrow
      : ((filters.value?.selectedCompany?.tradingPermissions ?? 0) &
          TradingPermission.BorrowerPermissions) >
        0
  );

  const ownInstruments = computed(
    () =>
      selectedMasterFilter.value === 'activeAndCompany' ||
      selectedMasterFilter.value === 'activeAndAgreement' ||
      selectedMasterFilter.value === 'activeAndSettlement'
  );

  const counterpartyInstruments = computed(
    () =>
      selectedMasterFilter.value === 'activeAndCompany' ||
      selectedMasterFilter.value === 'activeAndAgreement' ||
      selectedMasterFilter.value === 'activeAndSettlement' ||
      selectedMasterFilter.value === 'company' ||
      selectedMasterFilter.value === 'agreement' ||
      selectedMasterFilter.value === 'settlement'
  );

  const ownOrders = computed(
    () =>
      selectedMasterFilter.value !== 'company' && selectedMasterFilter.value !== 'activeAndCompany'
  );

  const securities = computed(() => {
    if (selectedMasterFilter.value === 'security') {
      return filters.value.security ? [filters.value.security] : null;
    }
    if (selectedMasterFilter.value === 'securities') {
      return filters.value.securities;
    }
    return null;
  });

  const oppositeSide = computed(() =>
    filters.value.tradeIntention === Side.BORROW ? Side.LOAN : Side.BORROW
  );

  const sideSelectorItems = computed(() => {
    const items: Array<{ text: string; value: Side }> = [];
    if (canLend.value) items.push({ text: 'Lend', value: Side.LOAN });
    if (canBorrow.value) items.push({ text: 'Borrow', value: Side.BORROW });

    updateTradeIntention();

    return items;
  });

  // traders can always filter the book but brokers only after selecting a 'view-as' company
  const canFilter = computed(() => !asBroker.value || filters.value.selectedCompany);

  const dataConfig = computed<DataSourceConfig>(() => {
    return {
      ordersPageLimit: ordersPageLimit.value,
      tradeIntention: filters.value.tradeIntention,
      rateType: rateTypeStore.marketplaceRateType,
      ownInstruments: ownInstruments.value,
      counterpartyInstruments: counterpartyInstruments.value,
      ownOrders: ownOrders.value,
      securities: securities.value,
      watchlist: filters.value.watchlist,
      orderType: filters.value.orderType,
      companies: filters.value.companies,
      agreements: filters.value.agreements,
      settlementType: filters.value.settlementType,
      companyIdFilter: filters.value.selectedCompany?.companyId,
      orderIntent: filters.value.orderIntent,
    };
  });

  function setAsBroker(value: boolean) {
    asBroker.value = value;
    emptyFilters();
  }

  function resetFilters() {
    selectedMasterFilter.value = null;
    gridFilterModel.value = null;
    initialState.value = null;
    emptyFilters();
    resetColumnState();
  }

  function resetColumnState() {
    if (gridApi.value === null) return;
    // reset column state to defaults (clears pin, order, sorting, show/hide)
    gridApi.value.applyColumnState({
      state: [],
      applyOrder: true,
    });
  }

  function emptyFilters() {
    filters.value = getEmptyFilters(filters.value.tradeIntention, filters.value.selectedCompany);
  }

  // keep Side if possible
  function updateTradeIntention() {
    if (filters.value.tradeIntention === Side.LOAN && !canLend.value) {
      filters.value.tradeIntention = Side.BORROW;
    }

    if (filters.value.tradeIntention === Side.BORROW && !canBorrow.value) {
      filters.value.tradeIntention = Side.LOAN;
    }
  }

  function refreshColumnState() {
    if (gridApi.value === null) {
      initialState.value = null;
      return;
    }
    initialState.value = gridApi.value.getState();
  }

  function onGridReady(params: GridReadyEvent): void {
    gridApi.value = params.api;
    if (gridFilterModel.value) {
      params.api.setFilterModel(gridFilterModel.value);
    }
    resizeColumns();

    params.api.addEventListener('firstDataRendered', () => {
      if (areGroupsExpanded.value) {
        params.api.expandAll();
      }

      // resize columns again after the first data is rendered,
      // the resizeColumns above does not calculate the column's width correctly when there is no data
      resizeColumns();
    });

    params.api.addEventListener('filterChanged', (event) => {
      gridFilterModel.value = event.api.getFilterModel();
    });

    params.api.addEventListener('stateUpdated', refreshColumnState);

    window.addEventListener('resize', resizeColumns);
    params.api.addEventListener('gridPreDestroyed', () => {
      window.removeEventListener('resize', resizeColumns);

      // won't properly destroy otherwise
      params.api.hidePopupMenu();
    });
  }

  function getGridApi(): GridApi {
    if (gridApi.value === null) throw new Error('Grid API not set yet');
    return gridApi.value;
  }

  function resizeColumns(): void {
    getGridApi().sizeColumnsToFit();
  }

  function expandGroups() {
    getGridApi().expandAll();
    areGroupsExpanded.value = true;
  }

  function collapseGroups() {
    getGridApi().collapseAll();
    areGroupsExpanded.value = false;
  }

  function isServerSideGroupOpenByDefault() {
    return areGroupsExpanded.value;
  }

  return {
    isGridLoaded,
    onGridReady,
    getGridApi,
    resizeColumns,
    expandGroups,
    collapseGroups,
    isServerSideGroupOpenByDefault,
    gridFilterModel,
    initialState,
    ordersPageLimit,
    selectedMasterFilter,
    filters,
    asBroker,
    canLend,
    canBorrow,
    ownInstruments,
    counterpartyInstruments,
    ownOrders,
    securities,
    oppositeSide,
    sideSelectorItems,
    canFilter,
    dataConfig,
    resetFilters,
    resetColumnState,
    emptyFilters,
    setAsBroker,
    updateTradeIntention,
  };
});

export type GridApiExplorerStore = ReturnType<typeof useGridApiExplorerStore>;
